/**
 * useHomeAssistantEntityState
 *
 * Returns the live HA entity state for a component bound via the
 * HomeAssistantEntityBindingModal. Idempotently triggers an instance-level
 * subscription on the cloud-relay state stream and an initial REST load, so
 * a bound component renders the real device state (e.g. light on/off) rather
 * than the default property fallback.
 *
 * Returns null when the component has no HA binding, or when the entity is
 * not yet loaded.
 */

import { useEffect } from "react";
import type { Component } from "@/types";
import { useHAStore } from "@/stores/integrations/ha-store";
import { extractHomeAssistantBinding } from "./binding";

const HA_ENTITY_POLL_INTERVAL_MS = 5_000;

export interface HomeAssistantLiveState {
  state: string;
  attributes: Record<string, unknown>;
  lastChanged?: string;
}

interface PollingRegistration {
  refCount: number;
  timerId: ReturnType<typeof window.setInterval>;
}

const pollingByInstance = new Map<string, PollingRegistration>();

function registerEntityPolling(
  instanceId: string,
  loadEntities: (instanceId: string, domain?: string) => Promise<void>,
): () => void {
  const existing = pollingByInstance.get(instanceId);
  if (existing) {
    existing.refCount += 1;
    return () => unregisterEntityPolling(instanceId);
  }

  const timerId = window.setInterval(() => {
    void loadEntities(instanceId);
  }, HA_ENTITY_POLL_INTERVAL_MS);
  pollingByInstance.set(instanceId, { refCount: 1, timerId });

  return () => unregisterEntityPolling(instanceId);
}

function unregisterEntityPolling(instanceId: string): void {
  const existing = pollingByInstance.get(instanceId);
  if (!existing) return;

  existing.refCount -= 1;
  if (existing.refCount > 0) return;

  window.clearInterval(existing.timerId);
  pollingByInstance.delete(instanceId);
}

/**
 * Read-side equivalent of executeHomeAssistantAction: hooks a component up to
 * its bound HA entity for display. Safe to call on every render — the
 * subscribe/load actions are guarded against duplicates by the store.
 */
export function useHomeAssistantEntityState(
  component: Pick<Component, "properties"> | null | undefined,
): HomeAssistantLiveState | null {
  const binding = component ? extractHomeAssistantBinding(component.properties) : null;
  const instanceId = binding?.instanceId;
  const entityId = binding?.entityId;

  const subscribeToInstance = useHAStore((s) => s.subscribeToInstance);
  const loadEntities = useHAStore((s) => s.loadEntities);

  useEffect(() => {
    if (!instanceId) return;
    subscribeToInstance(instanceId);
    void loadEntities(instanceId);
    return registerEntityPolling(instanceId, loadEntities);
  }, [instanceId, subscribeToInstance, loadEntities]);

  const entity = useHAStore((s) =>
    instanceId && entityId ? s.entitiesByInstance[instanceId]?.[entityId] : undefined,
  );

  if (!entity) return null;
  return {
    state: entity.state,
    attributes: entity.attributes,
    lastChanged: entity.last_changed,
  };
}

/**
 * Map an HA entity state string to a boolean (for toggle-style controls).
 * HA convention: "on" → true, "off" → false. Anything else (unavailable,
 * unknown, paused, locked, etc.) → null so callers can fall back to their
 * default property.
 */
export function haStateToBoolean(state: string | undefined): boolean | null {
  if (state === "on") return true;
  if (state === "off") return false;
  return null;
}
